import React, { Fragment, useState } from 'react';
import Table from '../../../components/table/table';
import axiosInstance from '../../../utility/axios-instance';
import SnackBar from '../../../components/SnackBar';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useSearchParams } from 'react-router-dom';

function DatePick ({handleChange, value}) {
    return (
        <div>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    label="Estimate Date" 
                    sx={{ 
                            "& .MuiOutlinedInput-input": {height: '2.5rem', padding: '0px 14px', fontSize: '14px'},
                            "& .MuiSvgIcon-root": { fontSize:"18px" },
                            "& .MuiOutlinedInput-notchedOutline": {borderColor:'#0000003b !important'},
                            "& .Mui-error": { color:'#00000099 !important' }
                    }}
                    inputFormat="YYYY-MM-DD"
                    size="small"
                    format="YYYY-MM-DD"
                    value={value}
                    onChange={(newValue) => {
                        handleChange(`${newValue.$y}-${newValue.$M+1}-${newValue.$D}`);
                    }} 
                    minDate={dayjs(Date.now())}
                />
            </LocalizationProvider>
        </div>
    )
}

const InventoriesWithPurchaseOrderDetails = () => {
    const [data, setData] = useState(null);
    const [snackBar, setSnackBar] = useState({ display: false, type: "success", message: "default message!" });
    const [searchParams] = useSearchParams();
    const searchData = searchParams.get('searchData');
    const [reRender, setReRender] = useState(false);

    const updateRow = (updateData, id) => {
        let index = data?.inventories?.findIndex(e => e.lineItemId === id);
        if (index >= 0) {
            setData((prev) => {
                const newData = {...prev};
                newData.inventories[index] = {...newData.inventories[index], ...updateData};
                return newData;
            })
        }
    }

    const updateEstimateDate = (date, lineItemId) => {
        axiosInstance.put(`/inventory/updateEstimateDate/${lineItemId}`, {estimate_date: date})
        .then(res => {
            updateRow(res.data.data, lineItemId);

            setSnackBar((prev) => {
                return {
                    ...prev,
                    display: true,
                    type: "success",
                    message: res?.data?.message ?? "Successfully updated"
                }
            });
            setReRender(!reRender);
        })
        .catch(err => {
            setSnackBar((prev) => {
                return {
                    ...prev,
                    display: true,
                    type: "error",
                    message: err?.response?.data?.message ?? "Something went wrong"
                }
            });
        })
    }

    const dueDifferenceDays = (date) => {
        const estimateDate = new Date(date);
        const currentDate = new Date();
        // Calculate the time difference in milliseconds
        const timeDifference = estimateDate.getTime() - currentDate.getTime();
        // Convert the time difference to days
        const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24)); // Rounding up the difference
    
        return (
            <p style={{ color: daysDifference >= 0  ? 'green' : 'red' }}>
                {daysDifference >= 0 ? 
                `${daysDifference} ${daysDifference > 1 ? 'days' : 'day'}`
                : `${Math.abs(daysDifference)} ${Math.abs(daysDifference) > 1 ? 'days' : 'day'} overdue`}
            </p>
        );
    };
    

    const columns = [
        {
            field: 'sku_code',
            headerName: 'SKU Code',
            flex:1,
            headerClassName: 'super-app-theme--header',
            filterable : true,
        },
        {
            field: 'product_name',
            headerName: 'Product Name',
            flex:2,
            headerClassName: 'super-app-theme--header',
            filterable : true,
        },
        { 
            field: 'locationCode', 
            headerName: 'Location Code', 
            flex:1,
            headerClassName: 'super-app-theme--header',
            filterable : true,
            type: 'enum',
            tableName: 'Locations',
            columnName: 'locationCode',
        },
        { 
            field: 'purchaseOrderName', 
            headerName: 'Purchase Order Name', 
            flex:1,
            headerClassName: 'super-app-theme--header',
            filterable : true,
        },
        { 
            field: 'overDueDays', 
            headerName: 'Overdue Days', 
            flex:.75,
            headerClassName: 'super-app-theme--header',
            filterable : false,
            renderCell: (params) => {
                return params?.row?.estimate_date ? dueDifferenceDays(params?.row?.estimate_date)  : '--'
            }
        },
        { 
            field: 'dueDays', 
            headerName: 'Due Days', 
            flex:.75,
            headerClassName: 'super-app-theme--header',
            filterable : false,
            renderCell: (params) => {
                return params?.row?.estimate_date ? dueDifferenceDays(params?.row?.estimate_date)  : '--'
            }
        },
        { 
            field: 'openQty', 
            headerName: 'Confirm Quantity', 
            flex:1,
            headerClassName: 'super-app-theme--header',
            filterable : false,
        },
        { 
            field: 'quantity', 
            headerName: 'Purchase QTY', 
            flex:1,
            headerClassName: 'super-app-theme--header',
            filterable : true,
        },
        { 
            field: 'discountedTotal', 
            headerName: 'Total Price', 
            flex:1,
            headerClassName: 'super-app-theme--header',
            filterable : false,
            renderCell: (params) => {
                return `₹${params.value}`
            }
        },
        { 
            field: 'estimate_date', 
            headerName: 'Estimate date', 
            flex:1.5,
            headerClassName: 'super-app-theme--header',
            filterable : false,
            renderCell: (params) => {
                return <div onClick={(event) => { event.stopPropagation() }}>
                        <DatePick
                            value={params?.row?.estimate_date ? dayjs(params?.row?.estimate_date) : null}
                            handleChange={(value) => {
                                console.log(value, params);
                                updateEstimateDate(value, params.row.lineItemId)
                            }}
                        />
                    </div>
            }
        },
        { 
            field: 'estimate_date_updated_by', 
            headerName: 'Updated By', 
            flex:1.5,
            headerClassName: 'super-app-theme--header',
            filterable : false,
        },
    ];

    return (
        <Fragment>
            <SnackBar snackBar={snackBar} setSnackBar={setSnackBar} />
        
            <div>
                <Table 
                    columns={columns} 
                    dataPosition={"inventories"} 
                    endpoint={`/inventory/getInventoriesWithPurchaseOrders`} 
                    rowId={'lineItemId'}
                    checkBoxSelection={false}
                    tabName={'Inventories With Purchase Order Details'}
                    setData={setData}
                    data={data}
                    createdByFilter={false}
                    isReactDispatch={true}
                    searchData={searchData}
                    reRender={reRender}
                    mobileResponsive={true}
                    exportEndpoint={'/exports/fetchInventoriesWithPurchaseOrders'}
                    exportFileName={`Inventories With Purchase Orders Report- ${new Date()}`}
                />
            </div>
        </Fragment>
    )
}
export default InventoriesWithPurchaseOrderDetails;